import { useMutation } from '@apollo/client';
import { Col, notification, Row } from 'antd';
import { CancelButton } from 'components/common/DefaultFormButton';
import { INPUT_TYPE, LABEL_TYPE } from 'components/common/Input/types';
import { Form, Formik } from 'formik';
import useCurrentLoggedInUser from 'hooks/apollo/queries/useCurrentLoggedInUser';
import useFormikRefHook from 'hooks/useFormikRefHook';
import { concat, get } from 'lodash';
import React, { ReactElement, useMemo } from 'react';
import { useLocation } from 'react-router';
import { UPDATE_USER } from 'services/graphql/mutations/users';
import { GET_SINGLE_USER } from 'services/graphql/queries/user.query';
import IntlMessages from 'utils/IntlMessages';
import * as Yup from 'yup';

import PortalProfileSaveButton from './PortalProfileSaveButton';
import { FormActionButtonStyled, InputComponentStyled, SideFieldContainerColStyled } from './styled';
import { IUpdateProfileTemplateProps } from './types';

// Input fields that can be increased in number have following format:
// - {name}-{number}
// => this function is to get the {name} and {number}
const getNameAndIndexOfAddMoreField: (str: string) => string[] = (str) => {
  const lastIndexOfHyphen = str.lastIndexOf('-');
  const baseName = lastIndexOfHyphen > 0 ? str.substr(0, lastIndexOfHyphen) : str;
  const inputIndex = lastIndexOfHyphen > 0 ? str.substr(lastIndexOfHyphen + 1) : '-1';
  return [baseName, inputIndex];
};

const UpdateProfileFormTemplate: React.FC<IUpdateProfileTemplateProps> = ({
  userData,
  initialFields,
  validation,
  onSubmit,
  onCancel,
  name,
  loading,
  refetchUserData,
  displaySaveOnFooter,
  inputComponentsFromOutside,
  hideSaveButton,
  portalButtonClassName,
  submitButtonLabel,
}) => {
  const location = useLocation();
  const { pathname } = location;
  const ref = useFormikRefHook(name);

  // MAIN CONTENT
  const formContents = useMemo(() => {
    const initValues: Record<string, any> = {};
    const componentsArray: ReactElement[] = [];
    const finalFieldsNames: string[] = [];
    initialFields.forEach((input) => {
      const inputName = input.name;
      const inputType = input.type || INPUT_TYPE.TEXT;
      const inputLabel = input.label;
      const inputOptions = get(input, 'options');
      const { sideFields } = input;
      const { disabled, value } = input;
      const defaultValue = input.defaultValue || get(userData, inputName) || '';
      const labelType = get(input, 'labelType', LABEL_TYPE.above);
      const { onChange } = input;
      initValues[inputName] = defaultValue;

      // Subfield, or inputs that are on the same row
      const defaultSideFieldSize = 8 / get(sideFields, 'length', 1);
      let totalSideFieldSize = 0;
      const manySideFieldNames: string[] = [];
      const SideFieldComponents =
        sideFields &&
        sideFields.map((sideField) => {
          const sideFieldSize = get(sideField, 'size', defaultSideFieldSize);
          const sideFieldName = sideField.name;
          const sideFieldDefaultValue = sideField.defaultValue || get(userData, sideFieldName, '');
          totalSideFieldSize += sideFieldSize;
          initValues[sideFieldName] = sideFieldDefaultValue;
          manySideFieldNames.push(sideFieldName);
          if (!sideField.disabled) {
            finalFieldsNames.push(sideFieldName);
          }
          return (
            <SideFieldContainerColStyled
              className='side-field'
              key={sideFieldName}
              span={24}
              lg={get(sideField, 'size', defaultSideFieldSize)}>
              <InputComponentStyled
                placeholder={sideField.placeholder}
                type={get(sideField, 'type', INPUT_TYPE.TEXT)}
                name={sideFieldName}
                label={inputLabel ? sideField.label : ''}
                labelType={sideField.labelType || labelType}
                inputOptions={sideField.inputOptions}
                inputSettings={sideField.inputSettings}
                checkboxGroupProps={sideField.checkboxGroupProps}
                defaultValue={sideFieldDefaultValue}
                value={sideField.value}
                disabled={sideField.disabled}
                addressSuggestion={sideField.addressSuggestion}
                addressSuggestionSubFields={sideField.addressSuggestionSubFields}
              />
            </SideFieldContainerColStyled>
          );
        });

      // push to component list
      componentsArray.push(
        <React.Fragment key={inputName}>
          <Row justify='space-between'>
            {SideFieldComponents}
            <Col span={24} lg={SideFieldComponents ? 24 - totalSideFieldSize : 24} className='side-field'>
              <InputComponentStyled
                // key={index}
                placeholder={input.placeholder}
                type={inputType}
                name={inputName}
                label={inputLabel}
                labelType={labelType}
                inputOptions={inputOptions}
                onChange={onChange}
                disabled={disabled}
                defaultValue={defaultValue}
                value={value}
                inputSettings={input.inputSettings}
                checkboxGroupProps={input.checkboxGroupProps}
                addressSuggestion={input.addressSuggestion}
                addressSuggestionSubFields={input.addressSuggestionSubFields}
              />
            </Col>
          </Row>
        </React.Fragment>,
      );
      if (!disabled) finalFieldsNames.push(inputName);
    });

    // get all the names of fields that are not deleted
    return [initValues, componentsArray, finalFieldsNames];
  }, [initialFields, userData]);

  const [initValues, inputComponentsArray, finalFieldsNames] = formContents;

  const { email } = useCurrentLoggedInUser(); // test nha
  const [updateUser, { loading: generalLoading }] = useMutation(UPDATE_USER, {
    refetchQueries: [
      {
        query: GET_SINGLE_USER,
        variables: {
          email,
        },
      },
    ],
  });

  const updateUserFunction = async (value: any) => {
    try {
      await updateUser({
        variables: {
          filter: {
            email,
          },
          updatedDoc: {
            ...value,
          },
        },
      });
      console.log('updated profileForm');
      // setShowSuccess(true);
      // dispatch(
      //   UserAction.fetchUserSuccess({
      //     ...value,
      //   }),
      // );
      notification.success({
        message: 'Update profile successfully!',
        key: 'updateProfile',
        duration: 2,
      });
      if (refetchUserData) refetchUserData();
    } catch (err) {
      console.error(err);
      notification.error({
        message: 'Update failed! Please try again.',
        key: 'updateProfile',
        duration: 2,
      });
    }
  };

  const onFormSubmit = (value: any) => {
    // filter the value based on deleted fields
    const finalSubmitValues = Object.entries(value).reduce((acc, entries: any) => {
      const k = entries[0];
      if (!finalFieldsNames.includes(k)) return acc;
      const v = entries[1];
      const [inputNameWithoutAddMoreIndex, indexOfInput] = getNameAndIndexOfAddMoreField(k);
      if (indexOfInput === '-1') {
        acc[k] = v;
      } else {
        // const [baseName, ...baseNameProps] = inputNameWithoutAddMoreIndex.split('-');
        acc[inputNameWithoutAddMoreIndex] = acc[inputNameWithoutAddMoreIndex]
          ? concat(acc[inputNameWithoutAddMoreIndex], v)
          : [v];
      }

      return acc;
    }, {} as Record<string, any>);

    if (onSubmit) return onSubmit(finalSubmitValues);
    return updateUserFunction(finalSubmitValues);
  };

  const yupValidation = Yup.object(validation);

  const containerClass =
    portalButtonClassName ||
    (pathname.split('/')[1] === 'setup-wizard'
      ? '.steps-content'
      : '.custom-tabs-content-active .gx-tab-content-header');

  return (
    <div>
      <Formik
        initialValues={initValues}
        onSubmit={onFormSubmit}
        validationSchema={yupValidation}
        innerRef={ref}
        initialErrors={{}}
        validateOnBlur>
        {(props) => (
          <Form className='gx-signin-form gx-form-row0'>
            {inputComponentsArray}
            {inputComponentsFromOutside?.(props)}
            <Row justify='end'>
              <Col span={24} className='buttons-holder'>
                <Row justify='start' className='form-buttons-wrapper'>
                  <PortalProfileSaveButton containerClass={containerClass} disabled={displaySaveOnFooter}>
                    <FormActionButtonStyled
                      isLoading={loading || generalLoading}
                      buttonType='primary'
                      className={`gx-mb-0 gx-mr-0 ${hideSaveButton ? 'gx-d-none' : ''} profile-save-button`}
                      onClick={() => {
                        props.handleSubmit();
                      }}>
                      {submitButtonLabel || <IntlMessages id='userProfile.saveProfile' />}
                    </FormActionButtonStyled>
                  </PortalProfileSaveButton>
                  {onCancel && (
                    <CancelButton className='gx-mb-0 gx-ml-3' htmlType='button' onClick={onCancel}>
                      <IntlMessages id='appModule.cancel' />
                    </CancelButton>
                  )}
                </Row>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default React.memo(UpdateProfileFormTemplate);
